import * as React from 'react';
import { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { useSnackbar } from 'notistack';
import { useDispatch, useSelector } from 'react-redux';

import { Icon } from '@iconify/react';
import heart from '@iconify/icons-mdi/heart';
import heartOutline from '@iconify/icons-mdi/heart-outline';
// mdi:stop
import stopIcon from '@iconify/icons-mdi/stop';
import moreVerticalFill from '@iconify/icons-eva/more-vertical-fill';

import { IconButton, MenuItem, ListItemText, Badge } from '@mui/material';

import FlightReviewRecalculet from './FlightReviewRecalculet';
import { editItem } from '../../fetch';
import FlightReviewShare from './FlightReviewShare';
import { updateFlightList } from '../flightList';
import { reportTripsActions } from '../../store';
import FlightReviewFlag from './FlightReviewFlag';
import FlightReviewComment from './FlightReviewComment';
import FlightReviewReplayVol from './FlightReviewReplayVol';
import { useTranslation } from '../../common/LocalizationProvider';
import FlightReviewDownload from './FlightReviewDownload';
import MenuPopover from '../MenuPopover';
import {
  selectAbonnementData,
  selectGroupSelectedId,
  selectLastFreeFlightUser,
  selectSession
} from '../../createSelectorUtils';

// ----------------------------------------------------------------------

FlightReviewSpeedDial.propTypes = {
  reportTrip: PropTypes.object.isRequired,
  privateAccount: PropTypes.bool.isRequired,
  state: PropTypes.object.isRequired,
  setState: PropTypes.func.isRequired
};

export default function FlightReviewSpeedDial({ reportTrip, privateAccount, state, setState }) {
  const ref = useRef(null);
  const t = useTranslation();

  const abonnementData = useSelector(selectAbonnementData);
  const lastFreeFlightUser = useSelector(selectLastFreeFlightUser);

  const abonnement = abonnementData
    ? true
    : lastFreeFlightUser === null ||
      (reportTrip &&
        new Date(reportTrip.startTime).getTime() <= new Date(lastFreeFlightUser).getTime());

  const session = useSelector(selectSession);
  const groupId = useSelector(selectGroupSelectedId);
  const permission = session.permission
    ? session.permission.find((item) => item.groupid === groupId) || {}
    : {};
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const [isOpen, setIsOpen] = useState(false);

  const handleAddFavorite = async () => {
    const body = {
      id: reportTrip.affectationVolsId,
      deviceId: reportTrip.deviceId,
      piloteId: reportTrip.driverUniqueId,
      instructeurId: reportTrip.instructeurUniqueId,
      startTime: reportTrip.startTime,
      endTime: reportTrip.endTime,
      groupId: reportTrip.groupId,
      attributes: {},
      comment: reportTrip.commentAffectationVols,
      flag: reportTrip.flagAffectationVols,
      favorite: !reportTrip.favoriteAffectationVols,
      remarquableTrace: reportTrip.pointRemarquableTrace > 0
    };

    const response = await editItem('affectationVols', body.id, body);
    if (response.ok) {
      enqueueSnackbar(body.favorite ? t('FL_Menu_Favorite_Added') : t('FL_Menu_Favorite_Removed'), {
        variant: 'success'
      });
      dispatch(reportTripsActions.update(await updateFlightList(body)));
    } else {
      const error = await response.text();
      const message = error.slice(0, error.indexOf('-'));
      enqueueSnackbar(message, { variant: 'error' });
    }
    setIsOpen(false);
  };

  return (
    <>
      <IconButton
        ref={ref}
        onClick={() => (state.playVol ? setState({ ...state, playVol: false }) : setIsOpen(true))}
        disabled={privateAccount}
      >
        {!state.playVol ? (
          <Icon icon={moreVerticalFill} width={23} height={23} />
        ) : (
          <Icon icon={stopIcon} width={23} height={23} />
        )}
      </IconButton>

      <MenuPopover
        open={isOpen}
        anchorEl={ref.current}
        onClose={() => setIsOpen(false)}
        sx={{ width: 200 }}
      >
        <FlightReviewReplayVol state={state} setState={setState} setIsOpenSpeedDial={setIsOpen} />

        <Badge badgeContent={abonnement ? null : 'pro'} color="secondary">
          <FlightReviewComment
            reportTrip={reportTrip}
            setIsOpenSpeedDial={setIsOpen}
            disabled={(reportTrip && reportTrip.affectationVolsId === 0) || !abonnement}
          />
        </Badge>

        <Badge badgeContent={abonnement ? null : 'pro'} color="secondary">
          <FlightReviewFlag
            reportTrip={reportTrip}
            setIsOpenSpeedDial={setIsOpen}
            disabled={(reportTrip && reportTrip.affectationVolsId === 0) || !abonnement}
          />
        </Badge>

        <Badge badgeContent={abonnement ? null : 'pro'} color="secondary">
          <MenuItem
            sx={{ color: 'text.secondary' }}
            onClick={handleAddFavorite}
            disabled={(reportTrip && reportTrip.affectationVolsId === 0) || !abonnement}
          >
            <Icon
              icon={reportTrip && reportTrip.favoriteAffectationVols ? heart : heartOutline}
              width={23}
              height={23}
            />
            <ListItemText
              primary={t('FR_Tooltip_AddToFavorites')}
              primaryTypographyProps={{ variant: 'body2' }}
            />
          </MenuItem>
        </Badge>

        <Badge badgeContent={abonnement ? null : 'pro'} color="secondary">
          <FlightReviewShare
            reportTrip={reportTrip}
            setIsOpenSpeedDial={setIsOpen}
            disabled={!abonnement}
          />
        </Badge>

        {(session.administrator || permission.status === 0) && (
          <FlightReviewRecalculet
            reportTrip={reportTrip}
            state={state}
            setState={setState}
            setIsOpenSpeedDial={setIsOpen}
          />
        )}

        <Badge badgeContent={abonnement ? null : 'pro'} color="secondary">
          <FlightReviewDownload
            reportTrip={reportTrip}
            state={state}
            setState={setState}
            setIsOpenSpeedDial={setIsOpen}
            disabled={!abonnement}
          />
        </Badge>
      </MenuPopover>
    </>
  );
}
